/**
 * PlanningPermissionApplicationComment entity model class
 *
 * This file is part of ePlan
 * (c) Kaden und Partner AG
 */
import { BaseModel } from './BaseModel';
import { BaseCollection } from './BaseCollection';
import type { Dayjs } from 'dayjs';
import type { MutationsObject } from '@kp/js-model';
import dayjs from 'dayjs';
import { useAuthUser } from '@/helpers/globals';
import { getI18n } from '@/helpers/i18n';

export class PlanningPermissionApplicationComment extends BaseModel {
    id: number | null = null;
    created: Dayjs | null = null;
    creator_id: number | null = null;
    creator_role: string | null = null;
    type: string | null = null;
    comment: string | null = null;
    planning_permission_application_id: number | null = null;
    intern: boolean | null = null;
    draft: boolean | null = null;
    source: string | null = null;

    // view field;
    creator: string | null = null;
    gemeinden_ids: Array<number> | null = null;
    gemeinden: Array<string> | null = null;
    document_ids: Array<number> | null = null;
    document_filenames: Array<string> | null = null;

    public mutations(): MutationsObject {
        return {
            id: (id: number | null) => (Number(id) > 0 ? Number(id) : null),
            creator_id: (id: number | null) => (Number(id) > 0 ? Number(id) : null),
            planning_permission_application_id: (id: number | null) => (Number(id) > 0 ? Number(id) : null),
            created: (value) => dayjs(value)
        };
    }

    getClassName(): string {
        return 'PlanningPermissionApplicationComment';
    }

    get isOwn() {
        return this.creator_id === useAuthUser()?.id && this.creator_role === useAuthUser()?.active_role;
    }

    get author() {
        if (this.creator_role === 'system') {
            return getI18n().t('eplan_thurgau');
        }
        if (this.creator) {
            return `${this.creator}`;
        } else if (this.source === 'boa') {
            return getI18n().t('kanton');
        } else {
            return '(' + getI18n().t('unbekannt') + ')';
        }
    }

    get typeDisplay() {
        if (!this.type) {
            return getI18n().t('information');
        }
        // type and subtype split:
        const matches = this.type.match(/^(.*?)::(.*)/);
        const type = matches ? matches[1] : this.type;
        const subtype = matches ? matches[2] : null;
        switch (type) {
            case 'status_change':
            case 'status_change_requested':
                switch (subtype) {
                    case 'submitted':
                        return getI18n().t('pg_eingereicht');
                    case 'in progress':
                        return getI18n().t('pg_entgegengenommen');
                    case 'demand documents gemeinde':
                        return getI18n().t('nachforderung_gemeinde');
                    case 'objection pending':
                        return getI18n().t('einsprachen_eingegangen');
                    case 'withdrawn':
                        return getI18n().t('planungsgeschaeft_zurueckgezogen');
                    default:
                        return getI18n().t(subtype || 'information');
                }
            case 'nachforderung_unterlagen_kanton':
                return getI18n().t('nachforderung_unterlagen_kanton');
            case 'hauptzirkulation_kanton_gestartet':
                return getI18n().t('mitteilung_vom_kanton');
            case 'zusaetzliche_sistierung':
                return getI18n().t('pg_sistiert');
            case 'sistierung_aufgehoben':
                return getI18n().t('sistierung_aufgehoben');
            default:
                return getI18n().t('information');
        }
    }
}

export class PlanningPermissionApplicationCommentCollection extends BaseCollection<PlanningPermissionApplicationComment> {
    protected modelCls = PlanningPermissionApplicationComment;

    public getModelClassName(): string {
        return 'PlanningPermissionApplicationComment';
    }
}

/**
 * This is a special collection that queries the read-only-endpoint PpaMessage/list,
 * which is a read-only view for relevant comments.
 */
export class PpaMessageCollection extends BaseCollection<PlanningPermissionApplicationComment> {
    protected modelCls = PlanningPermissionApplicationComment;

    public getModelClassName(): string {
        return 'PpaMessage';
    }
}
